import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, API_BASE_URL } from '../lib/api';
import { PageHeader, KpiCard, Loading, ErrorBox, Badge } from '../components/ui';

// Transaction status values from apps/api (modules/admin.ts): 'confirmed' (mapped from the
// payment's 'received'), 'pending', 'confirming', 'expired', 'failed'.
const TX_TONE: Record<string, 'success' | 'warn' | 'danger' | 'neutral'> = {
  confirmed: 'success',
  pending: 'warn',
  confirming: 'warn',
  expired: 'danger',
  failed: 'danger',
};
const txTone = (status: string) => TX_TONE[status] ?? 'neutral';

export function PaymentsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'transactions' | 'refunds'>('transactions');
  const summary = useQuery({ queryKey: ['admin-payments-summary'], queryFn: () => adminApi.paymentsSummary() });
  const transactions = useQuery({ queryKey: ['admin-transactions'], queryFn: () => adminApi.transactions(), enabled: tab === 'transactions' });
  const refunds = useQuery({ queryKey: ['admin-refunds'], queryFn: () => adminApi.refunds('initiated'), enabled: tab === 'refunds' });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-refunds'] });
  const approve = useMutation({ mutationFn: (id: string) => adminApi.refundApprove(id), onSuccess: invalidate });
  const markPaid = useMutation({ mutationFn: (id: string) => adminApi.refundMarkPaid(id), onSuccess: invalidate });

  if (summary.isLoading) return <Loading />;
  if (summary.error) return <ErrorBox error={summary.error} />;
  const kpis = ((summary.data as any)?.kpis ?? []) as any[];

  return (
    <div>
      <PageHeader title="Payments" subtitle="Nova Pay transfers and refunds" actions={<a className="btn-outline" href={`${API_BASE_URL}/v1/admin/transactions.csv`}>Export CSV</a>} />
      <div className="grid grid-cols-4 gap-3 mb-5">
        {kpis.map((k) => (
          <KpiCard key={k.key} label={k.label} value={k.formatted ?? String(k.value)} hint={k.hint} />
        ))}
      </div>
      <div className="flex gap-2 mb-3">
        <button className={tab === 'transactions' ? 'btn-primary' : 'btn-outline'} onClick={() => setTab('transactions')}>Transactions</button>
        <button className={tab === 'refunds' ? 'btn-primary' : 'btn-outline'} onClick={() => setTab('refunds')}>Refunds</button>
      </div>
      <div className="card">
        {tab === 'transactions' ? (
          <table>
            <thead><tr><th>Ref</th><th>Order</th><th>Customer</th><th>Amount</th><th>Provider</th><th>Status</th></tr></thead>
            <tbody>
              {((transactions.data as any)?.items ?? []).map((t: any) => (
                <tr key={t.id}><td>{t.ref}</td><td>{t.orderId}</td><td>{t.customer}</td><td>{t.amount?.formatted}</td><td>{t.provider}</td><td><Badge tone={txTone(t.status)}>{t.status}</Badge></td></tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table>
            <thead><tr><th>Order</th><th>Customer</th><th>Amount</th><th>Reason</th><th>Status</th><th /></tr></thead>
            <tbody>
              {((refunds.data as any)?.items ?? []).map((r: any) => (
                <tr key={r.id}>
                  <td>{r.orderId}</td><td>{r.customer}</td><td>{r.amount?.formatted}</td><td>{r.reason}</td>
                  <td><Badge tone="warn">{r.status}</Badge></td>
                  <td className="flex gap-1">
                    <button className="btn-outline" onClick={() => approve.mutate(r.id)} disabled={approve.isPending}>Approve</button>
                    <button className="btn-outline" onClick={() => markPaid.mutate(r.id)} disabled={markPaid.isPending}>Mark paid</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
